import { useState } from "react";
import { useCreateFacilitatorReflectionMutation } from "@/lib/facilitator/queries";
import type { StaffRoomDetailSegment } from "@/lib/staff/types";
import styles from "./reflection-composer.module.scss";

interface Props {
  roomId: string;
  segments: StaffRoomDetailSegment[];
  defaultSegmentId: string | null;
  /** When set, the composer is in reply mode and posts as a child of this id. */
  replyTo?: { id: string; preview: string; author: string } | null;
  onCancelReply?: () => void;
}

const MAX_LENGTH = 5000;

export function ReflectionComposer({
  roomId,
  segments,
  defaultSegmentId,
  replyTo,
  onCancelReply,
}: Props) {
  const [text, setText] = useState("");
  const [segmentId, setSegmentId] = useState<string>(
    defaultSegmentId ?? segments[0]?.id ?? "",
  );
  const mutation = useCreateFacilitatorReflectionMutation(roomId);

  const trimmed = text.trim();
  const disabled =
    !trimmed || trimmed.length > MAX_LENGTH || !segmentId || mutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (disabled) return;
    try {
      await mutation.mutateAsync({
        segmentId,
        reflectionText: trimmed,
        ...(replyTo ? { quotedReflectionId: replyTo.id } : {}),
      });
      setText("");
      if (onCancelReply) onCancelReply();
    } catch {
      // surfaces via mutation.isError
    }
  }

  return (
    <section className={styles.card} aria-labelledby="composer-heading">
      <header className={styles.head}>
        <h2 id="composer-heading" className={styles.title}>
          {replyTo ? "Reply" : "Post a reflection"}
        </h2>
        {replyTo ? (
          <button
            type="button"
            className={styles.cancelLink}
            onClick={onCancelReply}
          >
            Cancel
          </button>
        ) : null}
      </header>

      {replyTo ? (
        <div className={styles.quoted}>
          <span className={styles.quotedLabel}>
            Replying to {replyTo.author}
          </span>
          <span className={styles.quotedPreview}>{replyTo.preview}</span>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className={styles.form}>
        <label className={styles.label}>
          <span className={styles.labelText}>Segment</span>
          <select
            value={segmentId}
            onChange={(e) => setSegmentId(e.target.value)}
            className={styles.select}
            disabled={mutation.isPending}
          >
            {segments.map((s) => (
              <option key={s.id} value={s.id}>
                W{s.segmentNumber} — {s.title}
              </option>
            ))}
          </select>
        </label>

        <textarea
          className={styles.textarea}
          placeholder={
            replyTo
              ? "Write a reply to this reflection…"
              : "Share warmth, ask a question, or nudge the room…"
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={replyTo ? 3 : 5}
          maxLength={MAX_LENGTH}
          disabled={mutation.isPending}
        />

        <div className={styles.footer}>
          <span className={styles.charCount}>
            {trimmed.length}/{MAX_LENGTH}
          </span>
          <button
            type="submit"
            className={styles.submit}
            disabled={disabled}
            aria-label={replyTo ? "Post reply" : "Post reflection"}
          >
            {mutation.isPending ? "Posting…" : replyTo ? "Post reply" : "Post"}
          </button>
        </div>

        {mutation.isError ? (
          <div className={styles.error} role="alert">
            {(mutation.error as Error)?.message ?? "Failed to post."}
          </div>
        ) : null}
      </form>
    </section>
  );
}
